import type { usePermissions } from "../../hooks/usePermissions";

export interface MenuItem {
  to: string;
  icon: string;
  label: string;
  show: boolean;
  /** Подсвечивать пункт на этих адресах (по умолчанию — адрес пункта и вложенные) */
  active?: (p: string) => boolean;
  badge?: number;
}

export interface MenuGroup {
  key: string;
  title: string;
  icon: string;
  items: MenuItem[];
}

export const OFFICE = ["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"];

export const under = (base: string) => (p: string) => p === base || p.startsWith(base + "/");

export const itemActive = (it: MenuItem, pathname: string) => (it.active ? it.active(pathname) : under(it.to)(pathname));

/** Единый список разделов: из него строятся и боковое меню (компьютер), и нижнее (телефон) */
export function buildMenu(
  user: any,
  perms: ReturnType<typeof usePermissions>,
  badges: { wa: number; approvals: number },
) {
  const isOffice = OFFICE.includes(user?.role);
  const isAdmin = user?.role === "ADMIN";

  // Основные разделы, которыми пользуются каждый день, — наверху без группы
  const top: MenuItem[] = [
    { to: "/dashboard", icon: "📊", label: "Дашборд", show: isAdmin },
    { to: "/deals", icon: "💼", label: "Сделки", show: perms.canSeeDeals, active: (p) => under("/deals")(p) && !under("/deals/finished")(p) },
    { to: "/approvals", icon: "📝", label: "К согласованию", show: isAdmin, badge: badges.approvals },
    { to: "/work-orders", icon: "🛠", label: "Выполнение работ", show: true },
    { to: "/whatsapp", icon: "💬", label: "WhatsApp", show: true, badge: badges.wa },
    { to: "/clients", icon: "👤", label: "Клиенты", show: perms.canSeeClients },
    { to: "/companies", icon: "🏢", label: "Компании", show: perms.canSeeCompanies },
  ];

  const groups: MenuGroup[] = [
    {
      key: "money",
      title: "Касса и деньги",
      icon: "🧾",
      items: [
        { to: "/pos", icon: "🧾", label: "Касса", show: isOffice, active: (p) => under("/pos")(p) },
        { to: "/pos-report", icon: "📈", label: "Продажи и остатки", show: isOffice },
        { to: "/promotions", icon: "🎁", label: "Акции и бонусы", show: isOffice },
        { to: "/cash-debts", icon: "💵", label: "Касса / Долги", show: perms.canSeeCashDebts },
        { to: "/credit", icon: "💳", label: "Кредиты", show: perms.canSeeCredits },
        { to: "/deals/finished", icon: "✔️", label: "Завершённые сделки", show: perms.canSeeFinished },
        { to: "/reports", icon: "📊", label: "Отчёты", show: perms.canSeeReports || perms.canSeeEarnings },
      ],
    },
    {
      key: "stock",
      title: "Товары и склад",
      icon: "📦",
      items: [
        { to: "/products", icon: "📦", label: "Товары", show: perms.canSeeProducts },
        { to: "/warehouse", icon: "🏬", label: "Склад", show: perms.canSeeWarehouse },
        { to: "/keys", icon: "🔑", label: "Ключи", show: isOffice },
        { to: "/al-catalog", icon: "🗄️", label: "Склад 110", show: perms.canSeeIntegrations },
        { to: "/print", icon: "🖨", label: "Печать ценников", show: perms.canSeeProducts },
      ],
    },
  ];

  const system: MenuItem[] = [
    { to: "/integrations", icon: "🔌", label: "Интеграции", show: perms.canSeeIntegrations },
    { to: "/settings", icon: "⚙️", label: "Настройки", show: perms.canSeeSettings },
  ];

  return { top, groups, system };
}
